import { axiosPrivate } from 'api/client/classes/AxiosInstance';
import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react';
import { handleUnauthorizedAccess } from 'utils/auth';
import { createLogger } from 'utils/logger';
import useAuthentication from '../hooks/useAuthentication';

const logger = createLogger('AxiosProvider');

// Minimum time (ms) tab must be hidden before triggering proactive refresh on focus
const TAB_HIDDEN_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Context for Axios-related functionality
 * @type {React.Context}
 */
const AxiosContext = createContext({});

/**
 * Provider component that synchronizes Axios instance with authentication state
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} Provider component
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.8
 * @since 0.1.0-beta.7
 */
export const AxiosProvider = ({ children }) => {
  // Removed useNavigate import and usage
  const { authentication, setAuthentication } = useAuthentication();

  // Track when tab was hidden for visibility change handling
  const tabHiddenAtRef = useRef(null);

  /**
   * Handler for when token refresh starts
   * Sets refreshing flag immediately to prevent premature route redirects
   */
  const handleRefreshStart = useCallback(() => {
    logger.debug('Token refresh started. Setting refreshing flag.');
    setAuthentication(prevState => ({
      ...prevState,
      refreshing: true
    }));
  }, [setAuthentication]);

  /**
   * Handler for token refresh events from axios instance
   * Updates the authentication state in React context
   *
   * @param {Object} authData - New authentication data from token refresh
   */
  const handleTokenRefresh = useCallback(
    authData => {
      logger.debug('Token refresh detected. Updating authentication state.');

      // Update authentication state with new token and any other auth data
      setAuthentication(prevState => ({
        ...prevState,
        accessToken: authData.accessToken,
        // Preserve existing state values but override with new auth data
        ...(authData.id && { id: authData.id }),
        ...(authData.roles && { roles: authData.roles }),
        ...(authData.scopes && { scopes: authData.scopes }),
        // Additional values from auth data if available
        ...(authData.emails && { emails: authData.emails }),
        ...(authData.security && {
          security: {
            ...prevState.security,
            ...authData.security
          }
        }),
        ...(authData.session && {
          session: {
            ...prevState.session,
            ...authData.session,
            // Preserve device verification status
            device: {
              ...(prevState.session?.device || {}),
              ...(authData.session?.device || {}),
              isVerified:
                prevState.session?.device?.isVerified === true ||
                authData.session?.device?.isVerified === true
            }
          }
        }),
        refreshing: false
      }));
    },
    [setAuthentication]
  );

  /**
   * Handler for when token refresh fails (any error)
   * Clears the refreshing flag so routing decisions can proceed
   */
  const handleRefreshError = useCallback(
    error => {
      logger.warn('Token refresh failed:', error?.message || error);
      setAuthentication(prevState => ({
        ...prevState,
        refreshing: false
      }));
    },
    [setAuthentication]
  );

  /**
   * Handler for refresh failures that require re-authentication
   * Uses window.location instead of navigate hook
   */
  const handleRefreshFailure = useCallback(
    (/** error */) => {
      logger.warn('Maximum refresh attempts reached, redirecting to login');

      // Use the central unauthorized access handler WITHOUT navigate
      handleUnauthorizedAccess({
        reason: 'token_refresh_max_attempts'
        // No navigate parameter - will use window.location instead
      });
    },
    []
  );

  // Initialize axios instance when component mounts
  useEffect(() => {
    logger.debug('Initializing axios provider');

    // Set the current token if we have one
    if (authentication?.accessToken) {
      axiosPrivate.setAuthToken(authentication.accessToken);
    }

    // Register token refresh start listener (sets refreshing flag immediately)
    const unsubscribeRefreshStart =
      axiosPrivate.onRefreshStart(handleRefreshStart);

    // Register token refresh completion listener
    const unsubscribe = axiosPrivate.onTokenRefresh(handleTokenRefresh);

    // Register token refresh failure listener (clears refreshing flag on error)
    const unsubscribeRefreshFailure =
      axiosPrivate.onRefreshFailure(handleRefreshError);

    // Listen for the max refresh attempts error
    const originalReject = Promise.reject;
    Promise.reject = function (reason) {
      // Check for our custom maxRefreshAttemptsReached flag
      if (reason && reason.maxRefreshAttemptsReached) {
        handleRefreshFailure(reason);
      }
      return originalReject.call(this, reason);
    };

    return () => {
      // Clean up
      unsubscribeRefreshStart();
      unsubscribe();
      unsubscribeRefreshFailure();
      Promise.reject = originalReject;
    };
  }, [
    handleRefreshStart,
    handleTokenRefresh,
    handleRefreshError,
    handleRefreshFailure,
    authentication?.accessToken
  ]);

  // Handle tab visibility changes to proactively refresh tokens
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is now hidden - record the timestamp
        tabHiddenAtRef.current = Date.now();
        logger.debug('Tab hidden, recording timestamp');
      } else {
        // Tab is now visible again
        const hiddenAt = tabHiddenAtRef.current;
        tabHiddenAtRef.current = null;

        if (!hiddenAt) return;

        const hiddenDuration = Date.now() - hiddenAt;
        logger.debug(`Tab visible again after ${hiddenDuration}ms`);

        // Only trigger refresh if we have a session and were hidden long enough
        const hasStoredSession = (() => {
          try {
            return !!localStorage.getItem('auth_user_id');
          } catch {
            return false;
          }
        })();

        if (hasStoredSession && hiddenDuration >= TAB_HIDDEN_THRESHOLD_MS) {
          logger.info(
            'Tab was hidden for extended period, triggering proactive token refresh'
          );

          // Set refreshing flag before the axios refresh starts
          setAuthentication(prev => ({ ...prev, refreshing: true }));

          // Trigger a proactive refresh
          axiosPrivate.forceRefresh().catch(error => {
            logger.warn('Proactive refresh after tab focus failed:', error);
            // Clear refreshing flag on failure
            setAuthentication(prev => ({ ...prev, refreshing: false }));
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setAuthentication]);

  // Update axios token when auth token changes
  useEffect(() => {
    if (authentication?.accessToken) {
      axiosPrivate.setAuthToken(authentication.accessToken);
    } else if (authentication?.accessToken === null) {
      // Only remove when explicitly set to null (not undefined)
      axiosPrivate.removeAuthToken();
    }
  }, [authentication?.accessToken]);

  return (
    <AxiosContext.Provider value={{ axiosPrivate }}>
      {children}
    </AxiosContext.Provider>
  );
};

AxiosProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Custom hook to access the Axios context
 * @returns {Object} The axios context value
 */
export const useAxiosContext = () => useContext(AxiosContext);
